



























/* eslint-disable no-param-reassign, class-methods-use-this */

import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import difference from "lodash.difference";
import deepEqual from "fast-deep-equal";
import resize from "vuetify/es5/directives/resize";
import CdkFilterFormProvider from "@/components/CdkFilterForm/CdkFilterFormProvider.vue";
import useAuthentication from "@/composition/useAuthentication";
import FILTER_RULE_CHIP_ICONS from "@/constants/filterRuleChipIcons";
// @ts-ignore
import sortFilterRules from "@/utils/sortFilterRules";
import shortcuts from "@/constants/shortcuts";
import useSaveSearchButton from "@/composition/useSaveSearchButton";
import useMatomoEvent from "@/composition/useMatomoEvent";
import CdkSearch from "./CdkSearch.vue";

@Component({
  // @ts-ignore
  setup() {
    const { validateAndRetrieveToken } = useAuthentication();
    const { setShowFilterDialog } = useSaveSearchButton();

    const trackSaveSearchEvent = () => {
      useMatomoEvent({
        category: "Search",
        action: 'Click "Suche speichern"',
        name: "Saved Search"
      });
    };

    const trackSearchBarEnterEvent = () => {
      useMatomoEvent({
        category: "Search",
        action: "Search Bar Hit Enter",
        name: "Search Bar"
      });
      // @ts-ignore
      // eslint-disable-next-line no-unused-expressions
      window.Appcues?.track("Search Entered");
    };

    return {
      token: validateAndRetrieveToken,
      trackSaveSearchEvent,
      trackSearchBarEnterEvent,
      setShowFilterDialog
    };
  },
  inject: ["eventBus"],
  components: {
    CdkSearch,
    CdkFilterFormProvider
  },
  computed: {
    ...mapState("filterRules", {
      filterRules: (state: { filterRules: any[] }) =>
        sortFilterRules(state.filterRules),
      valuesMap: ({ valuesMap }: any) => valuesMap
    }),
    ...mapGetters("conceptItems", [
      "getAreaOptions",
      "getGenreOptions",
      "getRessortOptions"
    ])
  },
  methods: {
    ...mapMutations("filterRules", [
      "addFilterRule",
      "removeFilterRule",
      "removeFilterRulesByType",
      "setFilterRulesByType"
    ]),
    ...mapActions("filters", ["updateFilter"])
  },
  directives: { resize }
})
export default class CdkSearchProvider extends Vue {
  valuesMap: any;

  filterRuleChipIcons = FILTER_RULE_CHIP_ICONS; // eslint-disable-line

  cdkMaxWidth: string = "0";

  @Prop({
    type: Boolean,
    default: false
  })
  mobileSearch!: boolean;

  @Prop({
    type: Number,
    default: 0
  })
  containerWidth!: number;

  @Prop({
    type: Number,
    default: 0
  })
  containerHeight!: number;

  @Prop({
    type: Number,
    default: 0
  })
  width?: number;

  @Prop({
    type: String
  })
  appBarColor?: string;

  // NB: Mapping filterRule chip icons and custom CSS styling
  // REVIEW: needs cleaning up!
  get filterRulesWithIcons() {
    return this.filterRules.map(filterRule => ({
      ...filterRule,
      ...(filterRule.type === "FILTER_URGENCY" ||
      filterRule.type === "FILTER_IS_FAVORITE"
        ? {
            icon: this.filterRuleChipIcons[
              `${
                filterRule.type === "FILTER_URGENCY" ? "isUrgent" : "isFavorite"
              }`
            ]
          }
        : {}),
      ...(filterRule.type === "FILTER_IS_FAVORITE"
        ? { iconStyle: { padding: "0 9px 0 9px" } }
        : {})
    }));
  }

  get items(): any[] {
    return [
      ...(this.mobileSearch ? [{ header: "Erweiterte Suche:" }] : []),
      {
        display: "quick-filters",
        chips: [
          {
            label: this.$t("breakingNews"),
            color: "red",
            values: ["1", "2"],
            type: "FILTER_URGENCY",
            icon: this.filterRuleChipIcons.isUrgent,
            disabled: this.urgencyActive
          }
        ]
      },
      {
        options: [
          this.$t("genre:Meldung"),
          this.$t("genre:Überblick"),
          this.$t("genre:Zusammenfassung"),
          this.$t("genre:Achtung"),
          this.$t("genre:KORR-Bericht"),
          this.$t("genre:Interview"),
          this.$t("genre:Infobox"),
          this.$t("genre:Umfrage")
        ],
        values: [],
        placeholder: this.$t("selectGenre"),
        label: "Genre",
        type: "FILTER_GENRE",
        display: "select"
      },
      {
        options: [
          {
            label: this.$t("breakingNews"),
            value: ["1", "2"],
            disabled: false,
            matomoEvent: {
              category: "Advanced Search",
              action: 'Check "Eilmeldung" within Priorität',
              name: "Choose Priorität Eilmeldung"
            }
          },
          {
            label: this.$t("withoutPriority"),
            value: ["3", "4", "5", "6"],
            disabled: false,
            matomoEvent: {
              category: "Advanced Search",
              action: 'Check "Ohne Priorität" within Priorität',
              name: "Choose Priorität Ohne Priorität"
            }
          }
        ],
        values: [],
        label: this.$t("priority"),
        type: "FILTER_URGENCY",
        display: "checkbox"
      },
      {
        options: [
          this.$t("section:Kultur"),
          this.$t("section:Medien"),
          this.$t("section:Panorama"),
          this.$t("section:Politik"),
          this.$t("section:Sport"),
          this.$t("section:Wirtschaft"),
          this.$t("section:Wissen")
        ],
        values: [],
        placeholder: this.$t("selectSection"),
        label: this.$t("section"),
        type: "FILTER_CATEGORY",
        display: "select"
      },
      {
        options: [
          this.$t("region:Baden-Württemberg"),
          this.$t("region:Bayern"),
          this.$t("region:Berlin"),
          this.$t("region:Brandenburg"),
          this.$t("region:Bremen"),
          this.$t("region:Deutschland"),
          this.$t("region:Hamburg"),
          this.$t("region:Hessen"),
          this.$t("region:International"),
          this.$t("region:Mecklenburg-Vorpommern"),
          this.$t("region:Niedersachsen"),
          this.$t("region:Nordrhein-Westfalen"),
          this.$t("region:Rheinland-Pfalz"),
          this.$t("region:Saarland"),
          this.$t("region:Sachsen-Anhalt"),
          this.$t("region:Sachsen"),
          this.$t("region:Schleswig-Holstein"),
          this.$t("region:Thüringen")
        ],
        values: [],
        placeholder: this.$t("selectRegion"),
        label: this.$t("region"),
        type: "FILTER_AREA",
        display: "select"
      },
      {
        options: [
          {
            label: this.$t("text"),
            value: this.$t("text"),
            disabled: false,
            matomoEvent: {
              category: "Advanced Search",
              action: 'Click "Text" within Formate',
              name: "Click Formate Text"
            }
          },
          {
            label: this.$t("photo"),
            value: this.$t("photo"),
            disabled: false,
            matomoEvent: {
              category: "Advanced Search",
              action: 'Click "Foto" within Formate',
              name: "Click Formate Foto"
            }
          },
          {
            label: this.$t("graphic"),
            value: this.$t("graphic"),
            disabled: false,
            matomoEvent: {
              category: "Advanced Search",
              action: 'Click "Grafik" within Formate',
              name: "Click Formate Grafik"
            }
          },
          {
            label: "Audio",
            value: "Audio",
            disabled: true
          },
          {
            label: "Video",
            value: "Video",
            disabled: true
          }
        ],
        values: [],
        // FIXME
        // This label is not going through i18n as it seemingly creates a bug
        // where the FRC will be in your browser language and not your
        // userPref setting (if they differ) for some mysterious reason.
        label: "Format",
        type: "FILTER_ITEMCLASS",
        display: "checkbox"
      },
      {
        display: "submit"
      }
    ];
  }

  get favoriteOptions(): any[] {
    return [
      {
        text: this.$t("onlyFavorites"),
        value: true
      },
      {
        text: this.$t("noFavorites"),
        value: false
      },
      {
        text: this.$t("allContent"),
        value: undefined
      }
    ];
  }

  get itemclassOptions(): any[] {
    return [
      {
        text: this.$t("text"),
        value: "http://cv.iptc.org/newscodes/ninature/text"
      },
      {
        text: this.$t("photo"),
        value: "http://cv.iptc.org/newscodes/ninature/picture"
      },
      {
        text: this.$t("graphic"),
        value: "http://cv.iptc.org/newscodes/ninature/graphic"
      }
    ];
  }

  // REVIEW: Redundant data definitions for TypeScript compiler
  eventBus;

  getGenreOptions;

  getRessortOptions;

  getAreaOptions;

  filterRules;

  addFilterRule;

  removeFilterRule;

  removeFilterRulesByType;

  setFilterRulesByType;

  updateFilter;

  token: Function;

  @Watch("filterRules")
  onFilterRulesChanged(): void {
    this.items.map(item => ({
      ...item,
      value: this.filterRuleInputs[item.type]
    }));
    this.removeShortcutQuery();
  }

  removeShortcutQuery() {
    const { path, params, query } = this.$route;
    if (query.shortcut) {
      const shortcut = shortcuts.find(({ title }) => title === query.shortcut);
      const newQuery = { ...query };
      if (!deepEqual(shortcut.filterRules, this.filterRules)) {
        delete newQuery.shortcut;
      }
      this.$router.replace({
        path,
        params,
        query: newQuery
      });
    }
  }

  get genreOptions() {
    const options = this.getGenreOptions().map(option => {
      option.text = this.$t(`genre:${option.text}`);
      return option;
    });
    return options;
  }

  get ressortOptions() {
    const options = this.getRessortOptions().map(option => {
      option.text = this.$t(`section:${option.text}`);
      return option;
    });
    return options;
  }

  get areaOptions() {
    const options = this.getAreaOptions().map(option => {
      option.text = this.$t(`region:${option.text}`);
      return option;
    });
    return options;
  }

  get urgencyOptions() {
    return [
      { value: ["1", "2"], text: this.$t("breakingNews") },
      { value: ["3", "4", "5", "6"], text: this.$t("withoutPriority") }
    ];
  }

  get urgencyActive(): boolean {
    return this.filterRuleInputs.FILTER_URGENCY.some(
      rule => rule.indexOf("1") !== -1
    );
  }

  get isFavoriteActive(): boolean {
    return this.filterRuleInputs.FILTER_IS_FAVORITE.some(rule => rule === true);
  }

  get currentFilter() {
    return this.$store.getters["filters/getFilterById"](
      this.$route.query.filterId
    );
  }

  get isSaveFilterEnabled(): boolean {
    // TODO: Extract this logic, which is repeated in the filter change detection
    // for the app bar into a reusable function
    if (this.currentFilter) {
      return !deepEqual(
        // NB: compare against sorted rules
        [...sortFilterRules(this.currentFilter.filterRules)],
        [...this.filterRules]
      );
    }
    return this.filterRules.length > 0;
  }

  get filterRuleChips() {
    return this.filterRules.map(({ type, values, exclude }) => {
      // when the filter type isn't fulltext, map the uri to the value
      // to be displayed.
      if (type !== "FILTER_FULLTEXT") {
        // NB: each option item has to be populated with resolved values,
        // i.e. conceptItem URI values have to be some human readable text.
        const item = this.items.find(i => i.type === type) || {};
        let displayValues: any[] = [];
        if (type === "FILTER_URGENCY") {
          const hasUrgency = values.indexOf("1") !== -1;
          const hasNoUrgency = values.indexOf("3") !== -1;
          const tempValues: any[] = [];
          if (hasUrgency) tempValues.push(this.$t("breakingNews"));
          if (hasUrgency) displayValues.push(["1", "2"]);
          if (hasNoUrgency) tempValues.push(this.$t("withoutPriority"));
          if (hasNoUrgency) displayValues.push(["3", "4", "5", "6"]);
          values = [...tempValues];
          item.values = displayValues;
        } else if (type === "FILTER_IS_FAVORITE") {
          values = [this.favoriteOptions.find(() => values[0]).text];
        } else {
          values = this.mapFilterValuesToText(values, type);
          displayValues = values;
        }
        item.values = displayValues;
      }
      const [term] = values; // eslint-disable-line no-unused-vars
      const truncateOperator = /\*/;

      if (exclude === true) {
        // NB: Formatting for 'exclude' searches https://jira.mecom.de/browse/EC-247
        return `<b>${this.$t("not")}</b> ${term}`;
      }
      return values
        .map(text =>
          text.match(truncateOperator)
            ? // NB: Formatting for 'truncate' searches https://jira.mecom.de/browse/EC-221
              text.replace(truncateOperator, "<b>*</b>")
            : text
        )
        .join(` <b>${this.$t("or")}</b> `);
    });
  }

  set filterRuleChips(chipsAsText) {
    const removed = difference(this.filterRuleChips, chipsAsText);
    const added = difference(chipsAsText, this.filterRuleChips);

    if (added.length) {
      let [addedTerm] = added as string[]; // REVIEW: Safe typecasting?
      const startWildcard = /^\*/;
      const startsWithWildcard = !!addedTerm.match(startWildcard);

      if (startsWithWildcard) {
        addedTerm = addedTerm.replace(/^\*/, "");

        if (this.eventBus) {
          this.eventBus.$emit("appMessage", {
            model: true,
            text:
              "Sie haben eine Präfix-Trunkierung eingesetzt. Diese spezielle Trunkierung unterstützen wir zur Zeit noch nicht und haben sie deshalb aus Ihrer Suche entfernt."
          });
        }
      }

      const rules = this.getAddedFilterRules(addedTerm);
      rules.forEach(filterRule => {
        this.addFilterRule({ filterRule });
      });
    }

    if (removed.length) {
      const removedIndices = removed.map(text =>
        this.filterRuleChips.indexOf(text)
      );
      removedIndices.forEach(index => this.removeFilterRuleByIndex(index));
    }
  }

  get filterRuleInputs() {
    return {
      FILTER_FULLTEXT: this.getFilterRuleValuesByType("FILTER_FULLTEXT"),
      FILTER_GENRE: this.getFilterRuleValuesByType("FILTER_GENRE"),
      FILTER_URGENCY: this.getFilterRuleValuesByType("FILTER_URGENCY"),
      FILTER_CATEGORY: this.getFilterRuleValuesByType("FILTER_CATEGORY"),
      FILTER_ITEMCLASS: this.getFilterRuleValuesByType("FILTER_ITEMCLASS"),
      FILTER_IS_FAVORITE: this.getFilterRuleValuesByType("FILTER_IS_FAVORITE")
    };
  }

  getFilterRuleValuesByType(type) {
    const matchingRules = this.filterRules.filter(rule => rule.type === type);
    if (matchingRules.length) {
      if (type === "FILTER_URGENCY") {
        const hasUrgency = matchingRules.some(
          rule => rule.values.indexOf("1") !== -1
        );
        const hasNoUrgency = matchingRules.some(
          rule => rule.values.indexOf("3") !== -1
        );
        const temp = [];
        // @ts-ignore
        if (hasUrgency) temp.push(["1", "2"]);
        // @ts-ignore
        if (hasNoUrgency) temp.push(["3", "4", "5", "6"]);
        return temp;
      }
      return matchingRules.reduce((acc, curr) => acc.concat(curr.values), []);
    }
    return [];
  }

  getOptionsFromType(type) {
    let options;
    switch (type) {
      case "FILTER_GENRE":
        options = this.genreOptions;
        break;
      case "FILTER_CATEGORY":
        options = this.ressortOptions;
        break;
      case "FILTER_AREA":
        options = this.areaOptions;
        break;
      case "FILTER_URGENCY":
        options = this.urgencyOptions;
        break;
      case "FILTER_IS_FAVORITE":
        options = this.favoriteOptions;
        break;
      case "FILTER_ITEMCLASS":
        options = this.itemclassOptions;
        break;
      default:
        options = [];
    }
    return options;
  }

  mapFilterValuesToText(values, filterType) {
    const options = this.getOptionsFromType(filterType);

    let myValues = options
      .filter(option => values.some(val => val === option.value))
      .map(({ text }) => text);

    // NB: EC-621 not all conceptItem options (e.g. areas) are retrieved on initialisation,
    // but they are resolved in the category field and stored in the store: filterRules.valuesMap
    // when a classification tag is clicked
    // see @/mixins/setFilterRules > $_setFilterRules_handleTagClick
    if (!myValues.length) {
      myValues =
        this.valuesMap[filterType] && this.valuesMap[filterType][values.join()]
          ? [this.valuesMap[filterType][values.join()]]
          : []; // EC-653: hide URI values before conceptItems have been resolved
    }

    return myValues;
  }

  mapTextToFilterValues(values, filterType) {
    const options = this.getOptionsFromType(filterType);
    const myValues = values.map(
      value =>
        (options.find(option => option.text === value) || { value }).value
    );
    return myValues;
  }

  removeFilterRuleByIndex(index): void {
    const filterRulesToRemove = this.filterRules.filter((fr, i) => i === index);

    // NB: remove query "shortcut=myFavorites" from $route
    const ruleIsFavoriteIndex = this.filterRules.findIndex(
      ({ type, values }) =>
        type === "FILTER_IS_FAVORITE" && values.length && values[0] === true
    );
    const { shortcut } = this.$route.query;
    if (index === ruleIsFavoriteIndex && shortcut === "myFavorites") {
      const newQuery = { ...this.$route.query };
      delete newQuery.shortcut;
      this.$router.push({
        query: newQuery
      });
    }

    filterRulesToRemove.forEach(filterRule =>
      this.removeFilterRule({ filterRule })
    );
  }

  /**
   * getAddedFilterRules - Returns the array of fulltext FilterRules added by the
   * user in the searchbar.
   *
   * @param {String} addedTerm - The fulltext search term added by the user
   * @return {Array<Object>} filterRules - The array of FilterRules to be added to
   * the filterRules vuex module.
   */
  getAddedFilterRules(addedTerm) {
    // NB: operator patterns are case-insensitive (i.e. only caps)
    const excludeOperator = /^(NICHT )/;
    const orOperator = /^.*( ODER )+.*/;
    let rules = [
      {
        type: "FILTER_FULLTEXT",
        values: [addedTerm]
      }
    ];
    if (addedTerm.match(excludeOperator)) {
      rules = this.getExcludeFilterRules({
        addedTerm,
        excludeOperator
      });
    } else if (addedTerm.match(orOperator)) {
      rules = this.getOrFilterRules(addedTerm);
    }
    return rules;
  }

  /**
   * getOrFilterRules - Splits the values in the FilterRule object based
   * on a given fulltext search term delimited by 'ODER'.
   *
   * @param {String} addedTerm - The fulltext search term to be split
   * @return {Array} filterRules - The array containing the FilterRule
   * to be added.
   */
  getOrFilterRules(addedTerm) {
    return [
      {
        type: "FILTER_FULLTEXT",
        values: addedTerm.split(" ODER ")
      }
    ];
  }

  /**
   * getExcludeFilterRules - Formats the FilterRule object based on a
   * given fulltext search term to exclude.
   *
   * @param {String} addedTerm - The fulltext search term to exclude
   * @param {RegExp} excludeOperator - The RegExp pattern recognised as
   * the 'exclude' operator
   * @return {Array} filterRules - The array containing the FilterRule
   * to be added.
   */
  getExcludeFilterRules({ addedTerm, excludeOperator }) {
    return [
      {
        type: "FILTER_FULLTEXT",
        exclude: true,
        values: [addedTerm.replace(excludeOperator, "")]
      }
    ];
  }

  createFilterFromInput({ type, values }): void {
    if (!values.length) {
      this.removeFilterRulesByType({ type });
      return;
    }

    if (type === "FILTER_GENRE") {
      useMatomoEvent({
        category: "Advanced Search",
        action: 'Click "Genre"',
        name: "Category"
      });

      values.forEach(val => {
        useMatomoEvent({
          category: "Advanced Search",
          action: `Click "${val}" within Genre`,
          name: "Category"
        });
      });
    }

    if (type === "FILTER_CATEGORY") {
      useMatomoEvent({
        category: "Advanced Search",
        action: 'Click "Ressort"',
        name: "Category"
      });

      values.forEach(val => {
        useMatomoEvent({
          category: "Advanced Search",
          action: `Click "${val}" within Ressort`,
          name: "Category"
        });
      });
    }

    if (type !== "FILTER_URGENCY") {
      values = this.mapTextToFilterValues(values, type);
    }

    const filterRule = {
      type,
      values
    };
    this.setFilterRulesByType({ type, filterRules: [filterRule] });
  }

  overwriteFilter() {
    this.updateFilter({
      filter: {
        ...this.currentFilter,
        filterRules: this.filterRules
      },
      authJWT: this.token()
    });
    this.eventBus.$emit("frc-close");
    this.eventBus.$emit("close-mobile-frc");
  }

  onResize(): void {
    if (this.$refs.cdkSearch) {
      // @ts-ignore
      const element = this.$refs.cdkSearch.$el;
      let width;
      if (this.mobileSearch) {
        width = this.containerWidth;
      } else {
        width = element.offsetWidth;
      }
      this.cdkMaxWidth = `${width}px`;
    }
  }

  @Watch("containerWidth", { immediate: true })
  onContainerWidthChanged() {
    this.onResize();
  }

  mounted(): void {
    this.onResize();

    this.eventBus.$on("frc-toggled", async () => {
      await this.$nextTick();
      this.onResize();
    });
  }
}
